import Button from '@jetbrains/ring-ui/components/button/button'
import Checkbox from '@jetbrains/ring-ui/components/checkbox/checkbox'
import Dropdown from '@jetbrains/ring-ui/components/dropdown/dropdown'
import memoize from '@jetbrains/ring-ui/components/global/memoize'
import LoaderInline from '@jetbrains/ring-ui/components/loader-inline/loader-inline'
import {Directions} from '@jetbrains/ring-ui/components/popup/popup.consts'
import classNames from 'classnames'
import compose from 'lodash/flowRight'
import plur from 'plur'
import * as React from 'react'
import {useCallback, useMemo} from 'react'
import type {MeasuredComponentProps} from 'react-measure'
import type {$PropertyType} from 'utility-types'

import EntityLink from '../../../../containers/EntityLink'
import {withContentRect} from '../../../../containers/Measure'
import ProjectOrBuildTypeExternalLink from '../../../../containers/ProjectOrBuildTypeExternalLink'
import useEventCallback from '../../../../hooks/useEventCallback'
import {useBoolean} from '../../../../hooks/useSlice'
import {getFullPath, getOverviewData, getProject} from '../../../../selectors'
import {getProjectsData} from '../../../../selectors/projectTrees'
import {
  getProjectBuildTypeFilter,
  InvestigationStates,
  ROOT_PROJECT_ID,
  stringifyId,
} from '../../../../types'
import type {
  BuildStatus,
  BuildTypeId,
  TypeOfBuildType,
  FederationServerId,
  ProjectId,
  ProjectOrBuildTypeNode,
  ProjectOrBuildTypeStatusBase,
} from '../../../../types'
import {base_uri, internalProps} from '../../../../types/BS_types'
import {noop} from '../../../../utils/empty'
import {getSandbox} from '../../../../utils/fastdom'
import {isValue} from '../../../../utils/guards'
import {cmdOrCtrl} from '../../../../utils/keys'
import type {KeyValue, WritableKeyValue} from '../../../../utils/object'
import {useHint} from '../../../packages/Hints/Hints.hooks'
import EditProjectSidebar from '../../EditProjectSidebar/EditProjectSidebar'
import {resetOrderInProject} from '../../EditProjectSidebar/EditProjectSidebar.actions'
import {EditProjectSidebarContext} from '../../EditProjectSidebar/EditProjectSidebar.context'
import FavoriteStar from '../../FavoriteStar/FavoriteStar'
import IconButton from '../../IconButton/IconButton'
import Link from '../../Link/Link'
import Popup from '../../Popup/Popup.lazy'
import PopupMenu from '../../Popup/PopupMenu'
import ProjectOrBuildTypeIcon from '../../ProjectOrBuildTypeIcon/ProjectOrBuildTypeIcon'
import {EntityIconType} from '../../ProjectOrBuildTypeIcon/ProjectOrBuildTypeIcon.types'
import {ProjectsPopupContext} from '../../ProjectsPopup/ProjectsPopup.context'
import RouterLink from '../../RouterLink/RouterLink'
import RouterLinkWrapper from '../../RouterLinkWrapper/RouterLinkWrapper'
import SvgIcon from '../../SvgIcon/SvgIcon'
import {favoritesHint} from '../ProjectsTree.hint'

import StatusUpdater from './StatusUpdater/StatusUpdater'

import styles from './ProjectsTreeItem.css'

import {useAppDispatch, useAppSelector} from 'src/hooks/react-redux'

const FIRST_LEVEL_MARGIN_PX = 6
const LEVEL_MARGIN_PX = 16
export const TITLE_ITEM_HEIGHT = 38
export const FIRST_TITLE_ITEM_HEIGHT = 25
export const SERVER_ITEM_HEIGHT = 38
export const NOT_AUTHORIZED_SERVER_ITEM_HEIGHT = 65
export const REGULAR_ITEM_HEIGHT = 24
const SIDEBAR_FOOTER_HEIGHT = 41
const MULTISELECT_OFFSET = 10
const MULTISELECT_CHEVRON_OFFSET = 22
const POPUP_TIMEOUT_MS = 50
const popupDirections = [Directions.TOP_RIGHT, Directions.BOTTOM_RIGHT]
const CHANGES_COUNTER_LIMIT = internalProps['teamcity.internal.buildChangesPopupLimit']
type StarProps = {
  isStarred: boolean
  showPopup: boolean
  starHovered: boolean
  projectId: ProjectId | null | undefined
  onStar: (skipSubprojects?: boolean) => unknown
  onMouseEnter: (arg0: React.MouseEvent | null | undefined) => unknown
  onMouseLeave: (arg0: React.MouseEvent | null | undefined) => unknown
}
const Star = React.memo(function Star({
  isStarred,
  showPopup,
  starHovered,
  projectId,
  onStar,
  onMouseEnter,
  onMouseLeave,
}: StarProps) {
  const subprojectsCount = useAppSelector(state => {
    if (projectId == null) {
      return 0
    }

    const overviewData = getOverviewData(state)
    const projects = isStarred ? overviewData : getProjectsData(state)
    return projects.filter(id => getProject(state, id)?.parentProjectId === projectId).length
  })
  const isEdit = React.useContext(EditProjectSidebarContext)
  const star = (
    <FavoriteStar
      isStarred={isStarred}
      showEmpty
      className={classNames(styles.iconAction, {
        [styles.starButtonPopupShowed]: showPopup,
      })}
      iconWrapperClassName={classNames(styles.starIcon, {
        [styles.hovered]: starHovered,
      })}
      onStar={e => {
        if (!isEdit) {
          e?.stopPropagation()
        }

        e?.preventDefault()
        onStar(e?.ctrlKey || e?.metaKey)
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      title={subprojectsCount > 0 ? '' : null}
    />
  )
  const action = isStarred ? 'Remove from favorites' : 'Add to favorites'
  const subprojects = `${subprojectsCount} ${plur('subproject', subprojectsCount)}`

  const getLabel = (withSupbrojects: boolean) =>
    `${action} ${withSupbrojects ? 'with' : 'without'} ${subprojects}`

  return subprojectsCount > 0 ? (
    <Dropdown hoverMode clickMode={false} anchor={star}>
      <PopupMenu
        data={[
          {
            label: getLabel(true),
            description: 'click',
            onClick: () => onStar(false),
          },
          {
            label: getLabel(false),
            description: `${cmdOrCtrl}+click`,
            onClick: () => onStar(true),
          },
        ]}
      />
    </Dropdown>
  ) : (
    star
  )
})
export type MoveParams = {
  projectId: ProjectId | null | undefined
  buildTypeId: BuildTypeId | null | undefined
  shiftBy: number
}
type ItemType = 'buildType' | 'template' | 'project' | 'title' | 'server' | 'link' | 'action'
type BaseProps = {
  id: string
  itemType: ItemType
  href?: string | null | undefined
  icon?: string | null | undefined
  buildTypeId: BuildTypeId | null | undefined
  projectId: ProjectId | null | undefined
  name: string
  status: BuildStatus | null | undefined
  investigation: $PropertyType<ProjectOrBuildTypeStatusBase, 'investigation'> | null | undefined
  systemProblems: $PropertyType<ProjectOrBuildTypeStatusBase, 'systemProblems'> | null | undefined
  level: number
  collapsible?: boolean
  focusable?: boolean
  expanded?: boolean
  type?: TypeOfBuildType | null | undefined
  selected: boolean
  active: boolean
  searchPattern?: string | null | undefined
  hasOwnPending?: boolean
  pendingCount: number
  runningCount: number
  queuedCount: number
  isFavorites: boolean
  archived?: boolean
  paused?: boolean
  showStarAction?: boolean
  isStarred?: boolean | null | undefined
  authorized?: boolean | null | undefined
  loading?: boolean | null | undefined
  isScrolling?: boolean
  serverId: FederationServerId
  newFailedTestsCountsForRunningBuilds: ReadonlyArray<number> | null | undefined
  newFailedTestsCountForLatestBuild: number
  toggleOverview: (node: ProjectOrBuildTypeNode, on: boolean, skipSubprojects?: boolean) => unknown
  reloadProjects: () => unknown
  getWidth: () => number | null | undefined
  editable?: boolean
  isFirstSibling: boolean
  shouldFocus: boolean
  checked: boolean
  indeterminate: boolean
  isDisabled: boolean
  hasReorderedChildren: boolean
  onMove: (arg0: MoveParams) => unknown
  showPathInTitle?: boolean
  showPopup: boolean
  setShowPopup: (show: boolean) => void
}
type Props = BaseProps &
  MeasuredComponentProps & {
    isEdit: boolean
    isPopup: boolean
    isAdmin: boolean
    isMultiselect: boolean
    isAction: boolean
    onClick: () => unknown
    onChevronClick: () => unknown
    onFocus: () => unknown
    onCheck: (arg0: boolean) => unknown
    buildTypeUrlFormat: string | null | undefined
    templateUrlFormat: string | null | undefined
    projectUrlFormat: string | null | undefined
  }
type OuterProps = BaseProps & {
  index: number
  onClick: (index: number) => unknown
  onChevronClick: (index: number) => unknown
  onFocus?: (index: number) => unknown
  onCheck: (arg0: number, arg1: boolean) => unknown
}

const getOffsetForLevel: (level: number) => number = memoize(
  (level: number) => level * LEVEL_MARGIN_PX + FIRST_LEVEL_MARGIN_PX,
)
type HighlightingCache = WritableKeyValue<string, WritableKeyValue<string, React.ReactNode>>
type GetCustomUrlParams = {
  itemType: ItemType
  buildTypeId: BuildTypeId | null | undefined
  projectId: ProjectId | null | undefined
  buildTypeUrlFormat: string | null | undefined
  templateUrlFormat: string | null | undefined
  projectUrlFormat: string | null | undefined
}
export function getCustomUrl({
  itemType,
  buildTypeId,
  projectId,
  buildTypeUrlFormat,
  templateUrlFormat,
  projectUrlFormat,
}: GetCustomUrlParams): string | null | undefined {
  if (itemType === 'buildType' && buildTypeUrlFormat != null) {
    return buildTypeUrlFormat.replace('{id}', stringifyId(buildTypeId))
  }

  if (itemType === 'template' && templateUrlFormat != null) {
    return templateUrlFormat.replace('{id}', buildTypeId ?? '')
  }

  if (itemType === 'project' && projectUrlFormat != null) {
    return projectUrlFormat.replace('{id}', stringifyId(projectId))
  }

  return null
}

type ProjectsTreeItemInnerChevronButtonProps = {
  collapsible?: boolean
  expanded?: boolean
  hovered?: boolean
  offsetLeft: number
  isMultiselect?: boolean
  onClick?: (event: React.MouseEvent<any>) => void
  onMouseEnter?: () => void
  onMouseLeave?: () => void
}

function ProjectsTreeItemInnerChevronButton({
  collapsible,
  expanded,
  offsetLeft,
  hovered,
  isMultiselect,
  onClick,
  onMouseEnter,
  onMouseLeave,
}: ProjectsTreeItemInnerChevronButtonProps) {
  return (
    collapsible && (
      <IconButton
        special
        title={expanded ? 'Collapse' : 'Expand'}
        className={classNames(styles.chevronButton, {
          [styles.hovered]: hovered,
        })}
        style={{
          left: offsetLeft + (isMultiselect ? MULTISELECT_CHEVRON_OFFSET : 0),
        }}
        iconWrapperClassName={styles.chevronIcon}
        icon={expanded ? 'chevron-down' : 'chevron-right'}
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        data-test={expanded ? 'collapse-button' : 'expand-button'}
        tabIndex={-1}
      />
    )
  )
}

const ProjectsTreeItemInner = React.memo(function ProjectsTreeItemInner({
  shouldFocus,
  toggleOverview,
  itemType,
  projectId,
  buildTypeId,
  isStarred,
  reloadProjects,
  serverId,
  onChevronClick,
  onMove,
  id,
  type,
  name,
  icon,
  level,
  selected,
  active,
  searchPattern,
  hasOwnPending,
  pendingCount,
  runningCount,
  queuedCount,
  archived,
  showStarAction,
  isFavorites,
  newFailedTestsCountsForRunningBuilds,
  newFailedTestsCountForLatestBuild,
  isEdit,
  isPopup,
  isAdmin,
  isMultiselect,
  isAction,
  isFirstSibling,
  onClick,
  onFocus,
  measureRef,
  contentRect,
  expanded,
  collapsible,
  status,
  investigation,
  paused,
  systemProblems,
  href,
  getWidth,
  authorized,
  loading,
  editable,
  focusable,
  buildTypeUrlFormat,
  templateUrlFormat,
  projectUrlFormat,
  checked,
  indeterminate,
  isDisabled,
  onCheck,
  hasReorderedChildren,
  showPathInTitle,
  showPopup,
  setShowPopup,
  isScrolling,
}: Props): React.ReactElement | null {
  const dispatch = useAppDispatch()
  const popupRef = React.useRef<HTMLDivElement>(null)
  const elRef = React.useRef<HTMLDivElement>(null)
  const hoverTimeout = React.useRef<number>()
  const [starHovered, setStarHovered, unsetStarHovered] = useBoolean(false)
  const [chevronHovered, setChevronHovered, unsetChevronHovered] = useBoolean(false)
  const highlightingCache = React.useRef<HighlightingCache>({})
  React.useEffect(() => {
    if (shouldFocus && !elRef.current?.contains(document.activeElement)) {
      elRef.current?.parentElement?.querySelector<HTMLElement>('[data-sidebar-focusable]')?.focus()
    }
  }, [shouldFocus])
  const onMouseMove = useEventCallback((event: MouseEvent) => {
    if (showPopup && popupRef.current != null) {
      const {top, bottom, right} = popupRef.current.getBoundingClientRect()

      if (
        top > 0 &&
        (event.clientY < top - 1 || event.clientY > bottom + 1 || event.clientX > right)
      ) {
        setShowPopup(false)
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        removeGlobalListeners()
      }
    } else {
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      removeGlobalListeners()
    }
  })
  const clearHoverTimeout = React.useCallback(() => {
    if (hoverTimeout.current != null) {
      clearTimeout(hoverTimeout.current)
      hoverTimeout.current = undefined
    }
  }, [])
  const onDocumentMouseLeave = useEventCallback(() => {
    setShowPopup(false)
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    removeGlobalListeners()
    clearHoverTimeout()
  })
  const onWheel = useEventCallback(() => {
    if (showPopup && popupRef.current != null) {
      setShowPopup(false)
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      removeGlobalListeners()
    } else {
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      removeGlobalListeners()
    }
  })
  const addGlobalListeners = React.useCallback(() => {
    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('wheel', onWheel)
    document.body?.addEventListener('mouseleave', onDocumentMouseLeave)
  }, [onMouseMove, onWheel, onDocumentMouseLeave])
  const removeGlobalListeners = React.useCallback(() => {
    document.removeEventListener('mousemove', onMouseMove)
    document.removeEventListener('wheel', onWheel)
    document.body?.removeEventListener('mouseleave', onDocumentMouseLeave)
  }, [onMouseMove, onWheel, onDocumentMouseLeave])
  React.useEffect(() => removeGlobalListeners, [removeGlobalListeners])
  React.useEffect(() => clearHoverTimeout, [clearHoverTimeout])
  const openPopup = React.useCallback(() => {
    setShowPopup(true)
    addGlobalListeners()
  }, [setShowPopup, addGlobalListeners])
  const onMouseEnter = React.useCallback(
    (event: React.MouseEvent) => {
      const {clientY, currentTarget} = event
      hoverTimeout.current = window.setTimeout(async () => {
        if (clientY > window.innerHeight - SIDEBAR_FOOTER_HEIGHT - REGULAR_ITEM_HEIGHT) {
          if (currentTarget != null && currentTarget instanceof Element) {
            const sandbox = getSandbox(currentTarget)
            const fullyFit = await sandbox.measure(
              () =>
                currentTarget.getBoundingClientRect().bottom <=
                window.innerHeight - SIDEBAR_FOOTER_HEIGHT,
            )

            if (fullyFit) {
              sandbox.mutate(openPopup)
            }
          }
        } else {
          openPopup()
        }
      }, POPUP_TIMEOUT_MS)
    },
    [openPopup],
  )
  const onStarClick = React.useCallback(
    (skipSubprojects?: boolean) => {
      let node: ProjectOrBuildTypeNode | null | undefined

      if (itemType === 'project' && projectId != null) {
        node = {
          nodeType: 'project',
          id: projectId,
        }
      } else if (itemType === 'buildType' && buildTypeId != null) {
        node = {
          nodeType: 'bt',
          id: buildTypeId,
        }
      }

      if (node) {
        toggleOverview(node, !isStarred, skipSubprojects)
      }
    },
    [toggleOverview, itemType, projectId, buildTypeId, isStarred],
  )
  const onReloadProjectsButtonClick = React.useCallback(
    (event: React.MouseEvent<any>) => {
      event.stopPropagation()
      event.preventDefault()
      reloadProjects()
    },
    [reloadProjects],
  )
  const handleChevronClick = React.useCallback(
    (event: React.MouseEvent<any>) => {
      event.stopPropagation()
      event.preventDefault()
      onChevronClick()
    },
    [onChevronClick],
  )
  const handleMove = React.useCallback(
    () =>
      onMove({
        projectId,
        buildTypeId,
        shiftBy: -1,
      }),
    [projectId, buildTypeId, onMove],
  )

  const renderNameWithHighlighting = useCallback(() => {
    if (searchPattern == null) {
      return name
    }

    const cached = highlightingCache.current[name]?.[searchPattern]

    if (cached != null) {
      return cached
    }

    let output: React.ReactNode
    const query = new RegExp(searchPattern, 'igm')

    if (query.test(name)) {
      const tempArr: React.ReactNode[] = []
      name.split(query).forEach((str, index) => {
        if (str) {
          tempArr.push(
            <span
              key={index} /* eslint-disable-line react/no-array-index-key */
              className={
                new RegExp(searchPattern, 'igm').test(str) ? styles.highlighting : undefined
              }
            >
              {str}
            </span>,
          )
        }
      })
      output = tempArr
    } else {
      output = name
    }

    let cache = highlightingCache.current[name]

    if (cache == null) {
      cache = highlightingCache.current[name] = {}
    }

    cache[searchPattern] = output
    return output
  }, [name, searchPattern])

  const offsetLeft = getOffsetForLevel(level) + (isMultiselect ? MULTISELECT_OFFSET : 0)

  const renderStatusIcon = useCallback(() => {
    if (!isValue(EntityIconType, itemType)) {
      return null
    }

    let iconStatus: 'failed' | 'successful' | null | undefined
    let title
    let iconItemType

    if (!isEdit && !archived) {
      switch (status) {
        case 'SUCCESS':
        case 'WARNING':
          iconStatus = 'successful'
          break

        case 'FAILURE':
        case 'ERROR':
          iconStatus = 'failed'
          break

        default:
          iconStatus = null
          break
      }

      if (paused === true) {
        title = 'Build configuration was paused'
        iconItemType =
          iconStatus === 'failed' ? EntityIconType.PAUSED : EntityIconType.PAUSED_OUTLINE
      } else if (investigation === InvestigationStates.TAKEN) {
        title = 'Investigation is assigned'
        iconItemType = EntityIconType.INVESTIGATION
      } else {
        iconItemType = itemType
      }
    } else {
      iconItemType = itemType
    }

    return (
      <ProjectOrBuildTypeIcon
        title={title}
        type={iconItemType}
        composite={type === 'composite'}
        status={iconStatus}
        size="S"
      />
    )
  }, [archived, investigation, isEdit, itemType, paused, status, type])

  const commonClassNames = classNames(styles.projectsTreeItem, {
    [styles.selected]: selected,
    [styles.active]: active,
    [styles.archived]: archived,
    [styles.action]: itemType === 'action' || isAction,
    [styles.checkbox]: isMultiselect,
    [styles.disabled]: isDisabled,
  })
  const commonProps = {
    id,
    onClick,
    onFocus,
    'data-sidebar-focusable': 'true',
    className: commonClassNames,
  }
  const style = useMemo(
    () => ({
      paddingLeft: offsetLeft,
    }),
    [offsetLeft],
  )

  const isComposite = type === 'composite'
  const isExternalLink = isPopup || serverId !== base_uri
  const path = useAppSelector(state =>
    showPathInTitle && itemType === 'project'
      ? getFullPath(state, getProjectBuildTypeFilter({projectId}))
          .map(item => item.name)
          .join(' / ')
      : null,
  )
  const children = useMemo(
    () => (
      <div
        className={styles.content}
        data-test="sidebar-item"
        data-favorite={isFavorites}
        data-project-id={projectId}
        data-buildtype-id={buildTypeId}
        data-selected={selected}
      >
        <span className={styles.fakeChevronButton} />
        <span>
          {icon != null ? (
            <span>
              <SvgIcon className={styles.linkIcon} icon={icon} title={name} />
            </span>
          ) : (
            renderStatusIcon()
          )}
        </span>
        <span
          className={styles.name}
          ref={measureRef}
          aria-label={name}
          title={
            path ??
            ((contentRect.scroll?.width ?? 0) > (contentRect.client?.width ?? 0) ? name : '')
          }
        >
          {renderNameWithHighlighting()}
          {!isEdit && pendingCount > 0 ? (
            <span
              className={classNames(styles.pendingCounter, {
                [styles.withOwnPending]: hasOwnPending,
              })}
              title={`${pendingCount} pending change${
                pendingCount > 1 ? 's' : ''
              } in default branch`}
            >
              {pendingCount <= CHANGES_COUNTER_LIMIT
                ? ` +${pendingCount}`
                : ` ${CHANGES_COUNTER_LIMIT}+`}
            </span>
          ) : null}
          {!isEdit && !isComposite && newFailedTestsCountsForRunningBuilds?.length ? (
            <span
              className={styles.newFailedTestsCounter}
              title={`${newFailedTestsCountsForRunningBuilds[0]} new failed test${
                newFailedTestsCountsForRunningBuilds[0] > 1 ? 's' : ''
              } in the last running build in default branch`}
            >
              {` ↓${newFailedTestsCountsForRunningBuilds[0]}`}
            </span>
          ) : null}
          {!isEdit &&
          !isComposite &&
          newFailedTestsCountForLatestBuild > 0 &&
          !newFailedTestsCountsForRunningBuilds ? (
            <span
              className={styles.newFailedTestsCounter}
              title={`${newFailedTestsCountForLatestBuild} new failed test${
                newFailedTestsCountForLatestBuild > 1 ? 's' : ''
              } in the last build in default branch`}
            >
              {` ↓${newFailedTestsCountForLatestBuild}`}
            </span>
          ) : null}
        </span>
        {!isEdit && runningCount > 0 ? (
          <span
            className={styles.counter}
            title={`${runningCount} running build${runningCount > 1 ? 's' : ''}`}
          >
            <SvgIcon icon="spinner-static" className={styles.counterIcon} />
            <span className={styles.counterText}>{runningCount}</span>
          </span>
        ) : null}
        {!isEdit && queuedCount > 0 ? (
          <span
            className={styles.counter}
            title={`${queuedCount} queued build${queuedCount > 1 ? 's' : ''}`}
          >
            <SvgIcon icon="hourglass" className={styles.counterIcon} />
            <span className={styles.counterText}>{queuedCount}</span>
          </span>
        ) : null}
        {!isEdit && systemProblems != null && systemProblems.length > 0 && (
          <SvgIcon icon="warning" className={styles.warningIcon} title={systemProblems[0].title} />
        )}
        {projectId != null && hasReorderedChildren && (
          <Button
            text
            className={styles.reordered}
            onClick={() => dispatch(resetOrderInProject(projectId))}
          >
            {'Reset order'}
          </Button>
        )}
        {isEdit && isFavorites && (
          <IconButton
            disabled={isFirstSibling}
            className={styles.iconAction}
            icon="arrow-up"
            title="Move up"
            onClick={handleMove}
          />
        )}
        {archived !== true && (showStarAction === true || isEdit) && !isExternalLink ? (
          <Star
            projectId={projectId}
            isStarred={Boolean(isStarred)}
            showPopup={showPopup}
            starHovered={starHovered}
            onStar={onStarClick}
            onMouseEnter={setStarHovered}
            onMouseLeave={unsetStarHovered}
          />
        ) : null}
        {archived === true && <span className={styles.archiveLabel}>{'Archived'}</span>}
      </div>
    ),
    [
      archived,
      buildTypeId,
      contentRect.client?.width,
      contentRect.scroll?.width,
      dispatch,
      handleMove,
      hasOwnPending,
      hasReorderedChildren,
      icon,
      isComposite,
      isEdit,
      isExternalLink,
      isFavorites,
      isFirstSibling,
      isStarred,
      measureRef,
      name,
      newFailedTestsCountForLatestBuild,
      newFailedTestsCountsForRunningBuilds,
      onStarClick,
      path,
      pendingCount,
      projectId,
      queuedCount,
      renderNameWithHighlighting,
      renderStatusIcon,
      runningCount,
      selected,
      setStarHovered,
      showPopup,
      showStarAction,
      starHovered,
      systemProblems,
      unsetStarHovered,
    ],
  )
  function renderItem() {
    if (isMultiselect) {
      return (
        <Checkbox
          {...commonProps}
          containerClassName={commonProps.className}
          containerStyle={style}
          className=""
          cellClassName={styles.checkboxCell}
          labelClassName={styles.checkboxLabel}
          checked={checked}
          indeterminate={indeterminate}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onCheck(e.currentTarget.checked)}
          onClick={noop}
          disabled={isDisabled}
        >
          {children}
        </Checkbox>
      )
    }

    if (isEdit || isAction || itemType === 'action') {
      return (
        <div
          {...commonProps}
          style={style}
          role="button"
          tabIndex={0} // key events are handled on tree level
          onKeyDown={noop}
        >
          {children}
        </div>
      )
    }

    const customUrl = getCustomUrl({
      itemType,
      buildTypeId,
      projectId,
      buildTypeUrlFormat,
      templateUrlFormat,
      projectUrlFormat,
    })

    if (customUrl != null) {
      return (
        <RouterLinkWrapper>
          <Link {...commonProps} style={style} href={customUrl}>
            {children}
          </Link>
        </RouterLinkWrapper>
      )
    }

    const linkProps = {
      ...commonProps,
      style,
      buildTypeId,
      projectId,
      target: isExternalLink && !isPopup ? '_blank' : '_self',
      rel: 'noreferrer',
      children,
    }

    return isExternalLink ? (
      <ProjectOrBuildTypeExternalLink
        {...linkProps}
        serverId={serverId}
        isAdmin={isAdmin}
        isTemplate={itemType === 'template'}
      />
    ) : (
      <EntityLink {...linkProps} isAllProjects={!isFavorites && isStarred !== true} />
    )
  }

  function renderLink() {
    return href != null ? (
      <RouterLink {...commonProps} style={style} to={href}>
        <div className={styles.content}>
          <span className={styles.fakeChevronButton} />
          {icon != null ? (
            <span>
              <SvgIcon className={styles.linkIcon} icon={icon} title={name} />
            </span>
          ) : null}
          <span>{name}</span>
        </div>
      </RouterLink>
    ) : null
  }

  function renderPopups(item: React.ReactNode) {
    const width = getWidth()

    if (width == null) {
      return null
    }

    return (
      <>
        <Popup
          className={classNames(styles.popup, styles.nonInteractivePopup)}
          top={REGULAR_ITEM_HEIGHT}
          left={0}
          directions={popupDirections}
          minWidth={width}
          trapFocus={false}
        >
          <div data-inside-sidebar>{item}</div>
        </Popup>
        <Popup
          className={classNames(styles.popup, styles.interactivePopup)}
          top={REGULAR_ITEM_HEIGHT}
          left={0}
          directions={popupDirections}
          minWidth={width}
          trapFocus={false}
        >
          <div ref={popupRef} data-inside-sidebar>
            {item}
          </div>
        </Popup>
      </>
    )
  }

  if (itemType === 'title') {
    const titleClassName = classNames(styles.title, {
      [styles.selected]: selected,
    })
    const titleProps =
      focusable === true
        ? {
            className: classNames(titleClassName, {
              [styles.projectsTreeItem]: focusable,
            }),
            'data-sidebar-focusable': 'true',
            role: 'button',
            tabIndex: 0,
            onClick,
            // key events are handled on tree level
            onKeyDown: noop,
          }
        : {
            className: titleClassName,
            'data-hint-container-id': isFavorites ? favoritesHint.id : null,
          }
    return (
      <>
        <ProjectsTreeItemInnerChevronButton
          collapsible={collapsible}
          expanded={expanded}
          hovered={chevronHovered}
          onClick={handleChevronClick}
          onMouseEnter={setChevronHovered}
          onMouseLeave={unsetChevronHovered}
          offsetLeft={offsetLeft}
          isMultiselect={isMultiselect}
        />
        <div id={id} ref={elRef} style={{paddingLeft: getOffsetForLevel(0)}} {...titleProps}>
          <span className={styles.fakeChevronButton} />
          <span className={styles.name}>{name}</span>
          {editable === true && !isEdit && <EditProjectSidebar />}
        </div>
      </>
    )
  }

  if (itemType === 'server') {
    return (
      <div className={styles.server}>
        <label title={name} className={styles.serverTitle}>
          {name}
        </label>
        <div className={styles.serverDetails}>
          {loading !== true && authorized !== true ? (
            <>
              <label>{'Need to '}</label>
              <Link target="_blank" rel="noreferrer" href={name}>
                {'log in…'}
              </Link>
              <label>{' or '}</label>
              <Button
                className={styles.updateButton}
                onClick={onReloadProjectsButtonClick}
                data-inside-sidebar
                text
              >
                {'update data'}
              </Button>
            </>
          ) : null}
          {loading === true ? <LoaderInline>{'Projects are loading…'}</LoaderInline> : null}
        </div>
      </div>
    )
  }

  if (itemType === 'link') {
    return renderLink()
  }

  if (isScrolling) {
    return (
      <div className={commonClassNames}>
        <ProjectsTreeItemInnerChevronButton
          collapsible={collapsible}
          expanded={expanded}
          offsetLeft={offsetLeft}
          isMultiselect={isMultiselect}
        />
        <div style={style}>{children}</div>
      </div>
    )
  }

  const item = (
    <>
      <ProjectsTreeItemInnerChevronButton
        collapsible={collapsible}
        expanded={expanded}
        hovered={chevronHovered}
        onClick={handleChevronClick}
        onMouseEnter={setChevronHovered}
        onMouseLeave={unsetChevronHovered}
        offsetLeft={offsetLeft}
        isMultiselect={isMultiselect}
      />
      {renderItem()}
    </>
  )
  return (
    <div ref={elRef} data-test-itemtype={itemType}>
      {projectId !== ROOT_PROJECT_ID && !isEdit && !archived && (
        <StatusUpdater
          projectId={projectId}
          buildTypeId={buildTypeId}
          subscribeDelayMS={1000}
          updateOverviewParents
          withoutBranch
        />
      )}
      {showPopup && renderPopups(item)}
      <div
        onMouseEnter={isEdit ? undefined : onMouseEnter}
        onMouseLeave={isEdit ? undefined : clearHoverTimeout}
      >
        {item}
      </div>
    </div>
  )
})
const ProjectsTreeItem: React.ComponentType<OuterProps> = compose(
  React.memo,
  withContentRect(['scroll', 'client']),
)(function ProjectsTreeItem({
  index,
  onClick,
  onChevronClick,
  onFocus,
  onCheck,
  ...restProps
}: OuterProps & MeasuredComponentProps) {
  const {
    isPopup,
    isAdmin,
    isMultiselect = false,
    isAction = false,
    buildTypeUrlFormat,
    templateUrlFormat,
    projectUrlFormat,
  } = React.useContext(ProjectsPopupContext)
  return (
    <ProjectsTreeItemInner
      {...restProps}
      isEdit={React.useContext(EditProjectSidebarContext)}
      isPopup={isPopup}
      isAdmin={isAdmin}
      isMultiselect={isMultiselect}
      isAction={isAction}
      onClick={useEventCallback(() => onClick(index))}
      onChevronClick={useEventCallback(() => onChevronClick(index))}
      onFocus={useEventCallback(() => onFocus?.(index))}
      onCheck={useEventCallback(checked => onCheck(index, checked))}
      buildTypeUrlFormat={buildTypeUrlFormat}
      templateUrlFormat={templateUrlFormat}
      projectUrlFormat={projectUrlFormat}
    />
  )
})
export default ProjectsTreeItem
type StyledProps = OuterProps & {
  style: KeyValue<string, string | number>
}
export const StyledProjectsTreeItem: React.ComponentType<StyledProps> = React.memo<StyledProps>(
  function StyledProjectsTreeItem(props) {
    useHint(props.isFavorites && props.itemType === 'title' ? favoritesHint : null)
    const {style, ...restProps} = props
    return (
      <div style={style} role="row" className={styles.row}>
        <div role="gridcell" aria-selected={restProps.selected}>
          <ProjectsTreeItem {...restProps} />
        </div>
      </div>
    )
  },
)
